import type {
  PokerMttQueryVariables,
  PokerMultiTableTournaments,
  PokerTournamentNameColourVariables
} from '@ps/web-martech-cms-types';
import { createSelector } from '@rematch/select';
import type { Selector } from 'reselect';

import type { RootState, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  CommonQueryVariableOptions,
  ConfigQueryVariablesPayload,
  Tournament,
  TournamentColourMap
} from '@swf/poker';
import { generateMTTSelectors, mapQueryVariables } from '@swf/poker';

import { mapAllFilterVariables } from '../utils/filters';

import type { PokerMultiTableTournamentsState } from './types';

const getCmsVariables = (rootState: RootState, widget: PokerMultiTableTournaments) => {
  const isPlayMoney = select.currency.isPlayMoney(rootState);
  const uid = isPlayMoney
    ? widget.play_money_tournaments_filter_variables?.[0]?.uid
    : widget.real_money_tournaments_filter_variables?.[0]?.uid;

  return select.cms.getContentItem(rootState, uid) as PokerMttQueryVariables | undefined;
};

const getConfigTournNameColours = (rootState: RootState, widget: PokerMultiTableTournaments) => {
  const tournNameColourUid = widget.tournament_name_colour_variables?.[0]?.uid;

  const colorData = select.cms.getContentItem(
    rootState,
    tournNameColourUid
  ) as PokerTournamentNameColourVariables;

  return colorData;
};

export const selectors = (slice: Slicer<PokerMultiTableTournamentsState>) => ({
  ...generateMTTSelectors<PokerMultiTableTournamentsState>(slice),
  getActiveUiFilters: () => slice((state) => state.activeUiFilters),
  getFilterVariables: () =>
    createSelector(
      slice,
      (state: RootState) => select.currency.getPlayMode(state),
      (state: PokerMultiTableTournamentsState, playMode) => ({
        ...mapAllFilterVariables(state.activeUiFilters, playMode),
        ...state.activeFilters
      })
    ),
  getConfigQueryVariables(this: {
    getCommonVariables: Selector<RootState, CommonQueryVariableOptions>;
  }) {
    return createSelector(
      slice,
      this.getCommonVariables,
      (rootState: RootState, { widget }: ConfigQueryVariablesPayload<PokerMultiTableTournaments>) =>
        getCmsVariables(rootState, widget),
      (state: PokerMultiTableTournamentsState, commonVariables, cmsVariables) =>
        mapQueryVariables(
          { ...commonVariables, upcomingOnly: state.activeFilters.upcomingOnly },
          cmsVariables
        )
    );
  },
  getHighlightTournaments(this: { getTournamentList: Selector<RootState, Tournament[]> }) {
    return createSelector(
      slice,
      (rootState: RootState, widget: PokerMultiTableTournaments) =>
        getCmsVariables(rootState, widget)?.include.include_tournaments,
      (state, highlightTournamentIds) =>
        highlightTournamentIds?.reduce<Tournament[]>((acc, currentTournamentId) => {
          const tournament = state.tournamentsData[currentTournamentId];

          if (tournament) {
            acc.push(tournament);
          }

          return acc;
        }, []) ?? []
    );
  },
  getTournamentsNameColours() {
    return createSelector(
      slice,
      (rootState: RootState, widget: PokerMultiTableTournaments) =>
        getConfigTournNameColours(rootState, widget),
      (_, colourData) => {
        const colourMap = (colourData?.tournament_name_colour ?? []).reduce<TournamentColourMap>(
          (acc, item) => {
            const variable = item.colour_variable?.variable;
            const color = item.colour_variable?.colour;

            if (variable && color) {
              acc[variable] = color;
            }

            return acc;
          },
          {}
        );

        return colourMap;
      }
    );
  }
});
